import { useSubscription, useSuspenseQuery } from '@apollo/client/react';
import { Spinner } from '@chakra-ui/react';
import { Suspense, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { PageContentRoot } from '#components/page-content-root';
import { GET_DOCUMENT_BY_PATH } from '#graphql/queries/document';
import { DOCUMENT_UPDATED } from '#graphql/subscriptions/document-updated';
import { useAppConfig } from '#providers/app-config-provider';
import { useAppSettings } from '#providers/app-settings-provider';
import { useCurrentUser } from '#providers/user-provider';
import { isValidDocumentPath } from '#utils/document-path';

import { DocumentsEmptyState } from './documents-empty-state';
import { DocumentFilesPage } from './files/document-files-page';
import { DocumentHistory } from './history/document-history';
import { ViewDocument } from './view';

const DOCUMENTS_FILES_SUFFIX = '/files';

function parseDocumentsRoute(pathname: string): {
  documentPath?: string;
  isHistoryMode: boolean;
  isFilesPage: boolean;
} {
  const pathMatch = pathname.match(/^\/documents\/(.+)$/);
  if (!pathMatch) {
    return {
      isHistoryMode: false,
      isFilesPage: false,
    };
  }

  const fullPath = pathMatch[1];

  if (
    fullPath.endsWith(DOCUMENTS_FILES_SUFFIX) &&
    fullPath.length > DOCUMENTS_FILES_SUFFIX.length
  ) {
    const afterStrip = fullPath.slice(0, -DOCUMENTS_FILES_SUFFIX.length);
    const mixedHistory =
      afterStrip.endsWith('/history') ||
      afterStrip.includes('/history/compare/');
    if (mixedHistory) {
      let basePath: string | undefined;
      if (afterStrip.includes('/history/compare/')) {
        const compareMatch = afterStrip.match(/^(.+)\/history\/compare\/.+/);
        basePath = compareMatch ? compareMatch[1] : undefined;
      } else if (afterStrip.endsWith('/history')) {
        basePath = afterStrip.slice(0, -'/history'.length);
      }

      if (basePath) {
        return {
          documentPath: basePath,
          isHistoryMode: false,
          isFilesPage: true,
        };
      }
    }

    return {
      documentPath: afterStrip,
      isHistoryMode: false,
      isFilesPage: true,
    };
  }

  if (fullPath.includes('/history/compare/')) {
    const compareMatch = fullPath.match(/^(.+)\/history\/compare\/.+/);
    const documentPath = compareMatch ? compareMatch[1] : undefined;
    if (documentPath) {
      return {
        documentPath,
        isHistoryMode: true,
        isFilesPage: false,
      };
    }
  }

  const isHistoryMode = fullPath.endsWith('/history');
  let documentPath: string | undefined;
  if (isHistoryMode) {
    documentPath = fullPath.slice(0, -'/history'.length);
  } else {
    documentPath = fullPath || undefined;
  }

  return {
    documentPath,
    isHistoryMode,
    isFilesPage: false,
  };
}

/** Single document query; passes document + refetch to view/history. Defined at module level so React keeps a stable component identity and avoids re-mount loops. */
function DocumentPageWithData({
  documentPath,
  documentPathRegex,
  isHistoryMode,
  isFilesPage,
}: {
  documentPath: string;
  documentPathRegex: string;
  isHistoryMode: boolean;
  isFilesPage: boolean;
}) {
  const { config } = useAppConfig();
  const user = useCurrentUser();
  const { data, refetch: refetchDocument } = useSuspenseQuery(
    GET_DOCUMENT_BY_PATH,
    {
      variables: { path: documentPath },
    },
  );
  const result = data?.documentByPath;

  const lastServerEditTimestampRef = useRef(0);

  useSubscription(DOCUMENT_UPDATED, {
    variables: { path: documentPath },
    skip: !isValidDocumentPath(documentPath, documentPathRegex),
    onData: ({ data: subData }) => {
      const ts = subData.data?.documentUpdated?.editTimestamp;
      if (typeof ts !== 'number') return;

      if (ts <= lastServerEditTimestampRef.current) return;

      lastServerEditTimestampRef.current = ts;
      void refetchDocument();
    },
  });

  const path =
    result?.__typename === 'Document'
      ? result.path
      : result?.__typename === 'DocumentNotFound'
        ? (result.requestedPath ?? documentPath)
        : documentPath;

  if (isFilesPage) {
    return (
      <DocumentFilesPage
        documentResult={result}
        requestedPath={documentPath}
        apiHost={config.apiHost}
        userEmail={user.email}
      />
    );
  }

  if (isHistoryMode) {
    return (
      <DocumentHistory
        document={result?.__typename === 'Document' ? result : null}
        path={path}
        refetch={refetchDocument}
      />
    );
  }

  return (
    <ViewDocument
      key={path}
      document={result}
      path={path}
      refetch={refetchDocument}
      lastServerEditTimestampRef={lastServerEditTimestampRef}
    />
  );
}

export function DocumentsPage() {
  const location = useLocation();
  const { documentPathRegex } = useAppSettings();

  const route = useMemo(
    () => parseDocumentsRoute(location.pathname),
    [location.pathname],
  );

  const { documentPath, isHistoryMode, isFilesPage } = route;

  return documentPath ? (
    <Suspense
      fallback={
        <PageContentRoot
          flex={1}
          alignSelf="stretch"
          display="flex"
          alignItems="center"
          justifyContent="center"
          minH={0}
        >
          <Spinner size="xl" />
        </PageContentRoot>
      }
    >
      <DocumentPageWithData
        documentPath={documentPath}
        documentPathRegex={documentPathRegex}
        isHistoryMode={isHistoryMode}
        isFilesPage={isFilesPage}
      />
    </Suspense>
  ) : (
    <DocumentsEmptyState />
  );
}
